import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';
import useAxiosProtect from '../../hooks/useAxiosProtect';
import useAuth from '../../hooks/useAuth';

const AlertsContainer = props => {
    const axiosProtect = useAxiosProtect();
    const {auth} = useAuth();

    var init = useRef(true);

    const [page, setPage] = useState(0);
    const [subset, setSubset] = useState('unread');
    const [sites, setSites] = useState([]); 
    const [siteID, setSiteID] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [pageCount, setPageCount] = useState(0);

    const getSiteAccess = useCallback(async () => {
        if (!auth?.id) return;
        const { data } = await axiosProtect.get(`/auth/query/list-client-site-access?uid=${auth.id}`);
        
        console.log(data)
        let list = Array.isArray(data.sites) ? data.sites : [];

        axiosProtect.get(`/a/alerts/count-unread-by-site?uid=${auth.id}`).then(response => {
            for(let i = 0;i<list.length;++i){
                list[i].unread = response.data[list[i].id] > 0;
            }
            
            setSites(list);
        }).catch(error => {});

        if (!siteID && list.length) setSiteID(list[0].id);
    }, [auth?.id, siteID]);

    const getNotifications = useCallback(async () => {
        if (!auth?.id || !siteID) return;
        const url = `/a/alerts/notifications?uid=${auth.id}&page=${page}&subset=${subset}&siteID=${siteID}`;

        const { data } = await axiosProtect.get(url);
        let items = data?.items ?? [];
        const total = Number(data?.total ?? 0);

        setNotifications(items);
        setPageCount(Math.ceil(total / 10));
    }, [auth?.id, page, subset, siteID]);

    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(() => { if (!cancelled) getSiteAccess().catch(() => {}); }, 0);
        return () => { cancelled = true; clearTimeout(t); };
    }, [getSiteAccess]);

    useEffect(() => {
        if (!siteID) return;

        let cancelled = false;
        const t = setTimeout(() => { if (!cancelled) getNotifications().catch(() => {}); }, 0);

        return () => { cancelled = true; clearTimeout(t); };
    }, [getNotifications, siteID]);

    useEffect(() => { setPage(0); }, [subset, siteID]);

    const handleTabChange = (e, v) => {
        setSubset(v);
    }

    const formatDate = unixSeconds => {
        const date = new Date(parseFloat(unixSeconds) * 1000);

        return date.toLocaleString("default", {
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour12: false
        });
    }

    const generateDetails = item => {
        if(item.type === 'raw'){
            return `
                monitored ${item.utility} user 
                ${item.threshold_criteria === 'max' ? ' exceeded ' : ' fell below '} 
                ${item.threshold_value} ${item.utility === 'water' ? 'L' : item.utility === 'electricity' ? 'A' : 'm³'}
            `;
        }

        return `
            ${item.savings_kpi=== 'ghg' ? 'GHG' : item.savings_kpi} fell below  
            ${Math.round(100 * item.savings_threshold_percent)}% of target over the last ${item.sample_days} day${item.sample_days === 1 ? '' : 's'} for   
            opportunity "${item.opportunity_title}"
        `;
    }

    const toggleReadStatus = item => {
        axiosProtect.put(`/a/alerts/toggle-notification`, {
            uid: auth.id,
            type: item.type,
            notifId: item.notif_id
        }).then(response => {
            let tempItems = [];

            for(let i = 0;i<notifications.length;++i){
                if(notifications[i].notif_id === item.notif_id){
                    item.viewed = !item.viewed;
                    tempItems.push(item);
                }else{
                    tempItems.push(notifications[i]);
                }
            }

            setNotifications([...tempItems]);
        }).catch(error => {});
    }

    return (
        <div style={{padding:'2%', height: 'fit-content'}}>
            <FormControl sx={{ m: 1, width: { xs: "95%", sm: 300 } }}>
                <Select
                    labelId="demo-multiple-checkbox-label"
                    id="demo-multiple-checkbox"
                    value={siteID}
                    onChange={e => {
                        console.log(e.target.value)
                        setSiteID(e.target.value)
                    }}
                >
                    {sites.map(site => (
                        <MenuItem key={site.id} value={site.id}>
                            <span><span>{site.name}</span> {site.unread && <span style={{color:'red', fontWeight:'bold'}}>{`(NEW)`}</span>}</span>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box sx={{ width: "100%", backgroundColor: "rgba(245, 245, 245, 1)" }}>
                <Tabs
                    value={subset}
                    onChange={handleTabChange}
                    sx={{
                    "& .MuiTabs-indicator": {
                        backgroundColor: "rgb(0,177,168)",
                    },
                    "& .MuiTab-textColorSecondary.Mui-selected": {
                        color: "rgb(0,177,168)",
                    },
                    "& .MuiTab-textColorSecondary": {
                        color: "rgb(8,114,182)",
                    },
                    "& .MuiTab-root": {
                        // Responsive tab styling
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        minWidth: { xs: 80, sm: 120},
                        paddingX: { xs: 1, sm: 2 },
                    },
                    }}
                    aria-label="secondary tabs example"
                    variant="scrollable" 
                    scrollButtons="auto"
                >
                    <Tab value="unread" label="Unread" />
                    <Tab value="raw-data" label="Raw data alerts" />
                    <Tab value="opp" label="Opportunity alerts" />
                </Tabs>
            </Box>

            <br/>

            <List sx={{ width: '90%', bgcolor: 'background.paper', margin:'auto' }}>

            {
                notifications.length === 0 ? 
                <p>No {subset === 'unread' ? 'unread' : subset === 'raw-data' ? 'raw data' : 'opportunity'} alert notifications.</p>
                :

                notifications.map((notification, i) => (
                    <>
                        <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                            {
                                !notification.viewed ? 
                                <Avatar sx={{ bgcolor: '#DC4C64' }}>
                                    <ErrorIcon />
                                </Avatar>
                                :
                                <Avatar sx={{ bgcolor: '#54B4D3' }}>
                                    <TaskAltIcon />
                                </Avatar>
                            }
                            </ListItemAvatar>
                            <ListItemText
                                primary={<span style={{color:'rgb(8,114,182)', fontWeight:'bold'}}>{notification.type === 'opp' ? 'Opportunity' : 'Raw data'} alert</span>}
                                secondary={
                                    <React.Fragment>
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            sx={{ color: 'text.primary', display: 'inline' }}
                                        >
                                            <strong>{formatDate(notification.sent_at_epoch)}</strong>
                                        </Typography>
                                        <>
                                            {" — "} {generateDetails(notification)}
                                        </>
                                        <br/>
                                        <br/>
                                        {
                                            !notification.viewed &&
                                            <div>
                                            <FormControlLabel control={<Checkbox checked={notification.read} onClick={() => {
                                                toggleReadStatus(notification);
                                            }} />} 
                                                label={`Mark as ${notification.resolved ? 'un' : ''}read`} />
                                            </div>
                                        }
                                    </React.Fragment>
                                }
                            />
                        </ListItem>
                        {
                            (i + 1 < notifications.length) 
                            ? 
                            <Divider component="li" />
                            :
                            pageCount > 0 
                            ?
                            <Stack spacing={2} style={{marginTop:'2vh'}}>
                                <Pagination count={pageCount} page={page + 1} color="primary" onChange={(e, v) => {
                                    setPage(v - 1);
                                }} />
                            </Stack>
                            :
                            <></>
                        }
                    </>
                ))
            }
            </List>
        </div>
    );
}

export default AlertsContainer;